import { formatCount, formatPeriod } from "../reportFormat";
import {
  basisOf,
  DayWindow,
  describeChange,
  formatDays,
  formatLift,
  formatMetric,
  formatRate,
  quarterLabel,
  SCORECARD_SECTIONS,
  ScorecardUnit,
  SMALL_GROUP,
} from "../scorecardFormat";
import { CellSpec, INK, PdfDoc } from "./pdfDoc";
import { heading, HOUSE_WIDTH, HouseOptions, houseDoc, houseTable, note, paragraph, table, titleBlock, withMeaning } from "./houseStyle";

/**
 * The Scorecard as a PDF: the KPIs and retention rates for two stretches of
 * days side by side, each with what it means, then months after joining and
 * what makes members stay. What each number counts is in the backend's
 * docs/RETENTION_SCORECARD.md; the descriptions printed here come from the
 * server with the numbers.
 */

interface MetricValue extends DayWindow {
  value: number | null;
  numerator: number | null;
  denominator: number | null;
}

export interface ScorecardMetric {
  key: string;
  section: string;
  label: string;
  description: string;
  unit: ScorecardUnit;
  headline: boolean;
  higherIsBetter: boolean;
  target: number | null;
  shiftedDays: number;
  current: MetricValue;
  previous: MetricValue;
}

export interface CurveCohortData {
  cohort: string;
  members: number;
  months: Array<{ month: number; eligible: number; retained: number; rate: number | null }>;
}

export interface ScorecardPdfData {
  current: DayWindow;
  previous: DayWindow;
  timezone: string;
  region: string | null;
  metrics: ScorecardMetric[];
  curve: { since: string; cohortSize: "QUARTER" | "MONTH"; overall: CurveCohortData; cohorts: CurveCohortData[] } | null;
  drivers: {
    from: string;
    to: string;
    members: number;
    baseRate: number | null;
    signals: Array<{ key: string; label: string; withMembers: number; withRate: number | null; withoutRate: number | null; lift: number | null }>;
    scores: Array<{ score: number; andAbove: boolean; members: number; kept: number; rate: number | null }>;
  } | null;
}

const COLUMNS = [192, 82, 82, 82, HOUSE_WIDTH - 192 - 3 * 82];

const valueCell = (metric: ScorecardMetric, value: MetricValue): CellSpec => {
  const basis = basisOf(metric.unit, value.numerator, value.denominator);
  return { text: [formatMetric(metric.unit, value.value), basis].filter(Boolean).join("\n"), align: "right" };
};

/** The change in words as well as colour: "+1.2 pts, better". */
function changeCell(metric: ScorecardMetric): CellSpec {
  const change = describeChange(metric.unit, metric.current.value, metric.previous.value, metric.higherIsBetter);
  if (!change.label) return { text: "Nothing to compare", align: "right", style: { color: INK.muted } };
  const word = change.tone === "good" ? ", better" : change.tone === "bad" ? ", worse" : "";
  const color = change.tone === "good" ? INK.good : change.tone === "bad" ? INK.bad : INK.muted;
  return { text: `${change.label}${word}`, align: "right", style: { color, style: "bold" } };
}

/** What the number means, plus the dates it covers when it had to move back to settle. */
export function metricMeaning(metric: ScorecardMetric, stretch: { current: DayWindow; previous: DayWindow }): string {
  const parts = [metric.description];
  if (metric.shiftedDays > 0) {
    parts.push(
      `Counts ${formatDays(metric.current)} against ${formatDays(metric.previous)}: both moved back ${metric.shiftedDays} days so every member in them has had the time this measures.`,
    );
  } else if (metric.current.from !== stretch.current.from || metric.current.to !== stretch.current.to) {
    parts.push(`Counts ${formatDays(metric.current)} against ${formatDays(metric.previous)}.`);
  }
  const smallest = Math.min(metric.current.denominator ?? Infinity, metric.previous.denominator ?? Infinity);
  if (metric.unit === "RATE" && smallest > 0 && smallest < SMALL_GROUP) {
    parts.push(`Fewer than ${SMALL_GROUP} members: one or two people move this rate a lot.`);
  }
  return parts.join(" ");
}

export function buildScorecardPdf(data: ScorecardPdfData, options: HouseOptions): PdfDoc {
  const stretch = { current: data.current, previous: data.previous };
  const head = (first: string): CellSpec[] => [
    { text: first },
    { text: `This period\n${formatDays(data.current)}`, align: "right" },
    { text: `Compared with\n${formatDays(data.previous)}`, align: "right" },
    { text: "Change", align: "right" },
    { text: "Target", align: "right" },
  ];
  const row = (metric: ScorecardMetric): CellSpec[] => [
    { text: metric.label, style: { style: "bold" } },
    valueCell(metric, metric.current),
    valueCell(metric, metric.previous),
    changeCell(metric),
    { text: metric.target === null ? "" : formatRate(metric.target), align: "right" },
  ];
  const rows = (metrics: ScorecardMetric[], meanings: boolean) =>
    metrics.flatMap((metric) => withMeaning(row(metric), meanings && options.includeMeanings ? metricMeaning(metric, stretch) : null));

  const blocks = [
    ...titleBlock(
      "Scorecard",
      [`${formatDays(data.current)} compared with ${formatDays(data.previous)}`, `Region: ${data.region ?? "Everywhere"} · Days in ${data.timezone}`],
      options.preparedBy,
    ),
  ];
  if (options.includeMeanings) {
    blocks.push(
      paragraph(
        "The numbers that say whether members stay, for two stretches of days side by side, with the change between them. A rate is the stretch's totals divided, never an average of daily rates. Some rates need time to settle: a member who registered yesterday cannot yet have failed to come back in their second month. For those, both stretches move back by the same number of days, and the note under the number gives the dates it covers. Today is still in progress, so nothing counts it. Active means a member opened or used the app.",
        12,
      ),
    );
  }

  const headline = data.metrics.filter((metric) => metric.headline);
  if (headline.length) {
    blocks.push(heading("At a glance"), note("The five numbers that say most about whether members stay. Each is described in its section below."));
    blocks.push(table(houseTable(COLUMNS, head("Number"), rows(headline, false))));
  }
  for (const section of SCORECARD_SECTIONS) {
    const metrics = data.metrics.filter((metric) => metric.section === section.key);
    if (!metrics.length) continue;
    blocks.push(heading(section.title));
    if (section.note) blocks.push(note(section.note));
    blocks.push(table(houseTable(COLUMNS, head("Number"), rows(metrics, true))));
  }

  if (data.curve) {
    const { curve } = data;
    const months = curve.overall.months;
    const monthWidth = Math.floor(((HOUSE_WIDTH - 76 - 44) / Math.max(months.length, 1)) * 10) / 10;
    const label = (cohort: CurveCohortData) =>
      cohort.cohort === "all" ? "Everyone" : curve.cohortSize === "QUARTER" ? quarterLabel(cohort.cohort) : formatPeriod(cohort.cohort, "MONTH");
    blocks.push({ kind: "pageBreak" }, heading("Do members stay? Months after joining", 0));
    blocks.push(
      paragraph(
        `Of the members who registered since ${formatDays({ from: curve.since, to: curve.since })}, the share who still used the app in each month after registering. Month 1 is days 30 to 59, month 2 days 60 to 89, and so on; a member counts for a month only once it is over. In an app people keep, the line levels off; if it keeps falling toward zero, the app has not found its place in members' lives yet. The PMF audit's target for month 1 is 35%.`,
      ),
    );
    blocks.push(
      table(
        houseTable(
          [76, 44, ...months.map(() => monthWidth)],
          [{ text: "Registered" }, { text: "Members", align: "right" }, ...months.map((cell) => ({ text: `M${cell.month}`, align: "center" as const }))],
          [curve.overall, ...curve.cohorts].map((cohort) => [
            { text: label(cohort), style: cohort.cohort === "all" ? { style: "bold" as const } : undefined },
            { text: formatCount(cohort.members), align: "right" as const },
            ...cohort.months.map((cell) => ({ text: cell.eligible ? formatRate(cell.rate) : "", align: "center" as const })),
          ]),
          8,
        ),
      ),
      note("A blank cell: that month is not over yet for anyone in the group."),
    );
  }

  if (data.drivers && data.drivers.members > 0) {
    const { drivers } = data;
    blocks.push(heading("What makes members stay", 26));
    blocks.push(
      paragraph(
        `Of the ${formatCount(drivers.members)} members who registered ${formatDays({ from: drivers.from, to: drivers.to })}, ${formatRate(drivers.baseRate)} were still using the app 3 to 6 months later. Each row compares the members who did something in their first 14 days with those who did not; "compared with everyone" is how much likelier they were to stay than all of them. These are the eight early-adopter signals, worked out again on today's data.`,
      ),
    );
    blocks.push(
      table(
        houseTable(
          [196, 60, 84, 84, HOUSE_WIDTH - 196 - 60 - 168],
          [
            { text: "In their first 14 days, they…" },
            { text: "Members", align: "right" },
            { text: "Did it: still here 3–6 months later", align: "right" },
            { text: "Did not", align: "right" },
            { text: "Compared with everyone", align: "right" },
          ],
          drivers.signals.map((signal) => [
            { text: signal.label },
            { text: formatCount(signal.withMembers), align: "right" },
            { text: formatRate(signal.withRate), align: "right" },
            { text: formatRate(signal.withoutRate), align: "right" },
            { text: signal.lift === null ? "—" : `${formatLift(signal.lift)} as likely`, align: "right", style: { style: "bold" } },
          ]),
        ),
      ),
    );
    blocks.push(heading("By early-adopter score", 16), note("One point for each signal above. Three or more marks an early adopter."));
    blocks.push(
      table(
        houseTable(
          [196, 100, 100, HOUSE_WIDTH - 396],
          [{ text: "Score" }, { text: "Members", align: "right" }, { text: "Still here 3–6 months later", align: "right" }, { text: "Share", align: "right" }],
          drivers.scores.map((score) => [
            { text: `${score.andAbove ? `${score.score} or more` : score.score}${score.score === 3 ? " (early adopter from here)" : ""}` },
            { text: formatCount(score.members), align: "right" },
            { text: formatCount(score.kept), align: "right" },
            { text: formatRate(score.rate), align: "right" },
          ]),
        ),
      ),
    );
  }

  return houseDoc(`ILBP Scorecard ${data.current.from} to ${data.current.to}.pdf`, blocks, "ILBP Scorecard");
}
